<template>
    <div class="">
        <!-- Talleres de Escuela Orsai -->
        <div v-if="!loading && !error && talleres.length > 0">
            <div class="flex justify-between items-start md:items-center md:mb-8 flex-col md:flex-row">
                <h2 class="font-inter font-bold text-[16px] leading-[128%] tracking-[0%] md:text-[24px] md:leading-[120%]">Talleres que te pueden interesar</h2>
                
                <!-- Filtros -->
                <div class="md:px-4 self-end hidden md:block">
                    <div class="flex items-center space-x-4 py-3">
                        <!-- Filtro por mes -->
                        <div class="relative group">
                            <button @click="toggleSubMenu('mes')"
                                class="px-4 py-2 text-gray-700 hover:text-black hover:bg-gray-200 rounded-md transition-colors flex items-center gap-1"
                                :class="{ 'bg-gray-200 text-black': openSubMenu === 'mes' }">
                                {{ filtros.mes || 'Mes' }}
                                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 transition-transform"
                                    :class="{ 'rotate-180': openSubMenu === 'mes' }" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            <div v-if="openSubMenu === 'mes'"
                                class="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                                <button v-for="mes in meses" :key="mes"
                                    @click="seleccionarMes(mes)"
                                    class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-black">
                                    {{ mes }}
                                </button>
                                <button @click="seleccionarMes('')"
                                    class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-black">
                                    Todos los meses
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Overlay para cerrar menús -->
            <div v-if="openSubMenu" class="fixed inset-0 z-40" @click="closeAllMenus"></div>

            <!-- Mensaje cuando no hay resultados -->
            <div v-if="talleresFiltrados.length === 0" class="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 class="text-xl font-semibold text-gray-800 mb-2">No hay talleres disponibles</h3>
                <p class="text-gray-600 mb-4">
                    <span v-if="filtros.mes">
                        No encontramos talleres programados para {{ filtros.mes.toLowerCase() }}.
                    </span>
                    <span v-else>
                        No hay talleres disponibles en este momento.
                    </span>
                </p>
                <p class="text-gray-500 text-sm mb-6">
                    Te sugerimos revisar otros meses o volver más tarde para ver nuevas propuestas.
                </p>
                <button @click="limpiarFiltros" 
                    class="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Ver todos los talleres
                </button>
            </div>

            <div v-else class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 mt-4">
                <div v-for="taller in talleresFiltrados" :key="taller.id" class="post-item max-w-md w-full mx-auto">
                    <!-- Desktop/Tablet view -->
                    <div class="bg-white rounded-2xl shadow-md border border-gray-200 flex flex-col hidden md:block">
                        <a :href="taller.botonInfo && taller.botonInfo.trim() !== '' ? `/inscripciones/${getInscripcionesId(taller.slug)}` : taller.linkTienda" 
                            :target="taller.botonInfo && taller.botonInfo.trim() !== '' ? '_self' : '_blank'"
                            class="block w-full aspect-square overflow-hidden rounded-t-2xl">
                            <template v-if="taller.urlAfiche">
                                <img :src="taller.urlAfiche" 
                                    :alt="taller.lema"
                                    class="w-full h-full object-cover rounded-t-2xl"
                                    @error="handleImageError" />
                            </template>
                            <div v-else class="w-full aspect-square bg-gray-100 flex items-center justify-center rounded-t-2xl">
                                <span class="text-gray-400">Sin imagen</span>
                            </div>
                        </a>
                        <div class="flex gap-2 px-4 pt-2">
                            <!-- Fecha del taller -->
                            <span class="text-xs font-normal leading-tight uppercase">
                                Desde el <span class="font-bold">{{ taller.inicio }}</span>
                            </span>
                        </div>
                        <!-- Título del taller -->
                        <h3 class="text-base font-bold leading-tight text-black px-4 pt-2">{{ taller.lema }}</h3>
                        <!-- Docente -->
                        <p class="text-sm font-normal leading-tight text-gray-600 px-4">{{ taller.docente }}</p>
                        
                        <!-- Precio -->
                        <div class="px-4 pt-2">
                            <span class="text-md font-bold text-gray-900">ARS {{ taller.precio }}</span>
                            <span v-if="taller.precioUSD" class="text-sm text-gray-500 block">USD {{ taller.precioUSD }}</span>
                        </div>
                        
                        <!-- Botones -->
                        <div class="px-4 pb-4 mt-4">
                            <div class="flex flex-col gap-2">
                                <!-- Botón principal: Reservar -->
                                <a
                                    :href="taller.botonInfo && taller.botonInfo.trim() !== '' ? `/inscripciones/${getInscripcionesId(taller.slug)}` : taller.linkTienda"
                                    :target="taller.botonInfo && taller.botonInfo.trim() !== '' ? '_self' : '_blank'"
                                    class="text-white text-sm font-semibold leading-none text-center py-3 px-4 rounded-lg uppercase transition-colors w-full"
                                    :style="taller.colorBoton ? { backgroundColor: '#' + taller.colorBoton } : {}"
                                >
                                    {{ taller.boton }}
                                </a>
                            </div>
                        </div>
                    </div>

                    <!-- Mobile view -->
                    <div class="bg-white rounded-2xl shadow-md border border-gray-200 flex flex-row md:hidden">
                         <!-- Imagen del taller (mobile) -->
                        <a :href="taller.botonInfo && taller.botonInfo.trim() !== '' ? `/inscripciones/${getInscripcionesId(taller.slug)}` : taller.linkTienda" 
                            :target="taller.botonInfo && taller.botonInfo.trim() !== '' ? '_self' : '_blank'"
                            class="block w-1/2 aspect-square overflow-hidden rounded-l-2xl">
                            <template v-if="taller.urlAfiche">
                                <img :src="taller.urlAfiche" 
                                    :alt="taller.lema"
                                    class="w-full h-full object-cover rounded-l-2xl"
                                    @error="handleImageError" />
                            </template>
                            <div v-else class="w-full aspect-square bg-gray-100 flex items-center justify-center rounded-l-2xl">
                                <span class="text-gray-400">Sin imagen</span>
                            </div>
                        </a>
                         <!-- Contenido del taller (mobile) -->
                         <div class="flex flex-col justify-between p-4 w-1/2">
                            <div>
                                <!-- Fecha del taller (mobile) -->
                                <span class="text-[10px] font-normal leading-[130%] uppercase">Desde el <span class="font-bold">{{ taller.inicio }}</span></span>
                                <!-- Título del taller (mobile) -->
                                <h3 class="text-base font-bold leading-[120%] text-black mt-1">{{ taller.lema }}</h3>
                                <!-- Docente (mobile) -->
                                <p class="text-[10px] font-normal leading-[130%] text-gray-600 uppercase mt-1">{{ taller.docente }}</p>
                            </div>
                            <div class="mt-auto">
                                <!-- Precio ARS (mobile) -->
                                <div class="mb-3">
                                    <span class="text-sm font-bold text-gray-900 block">ARS {{ taller.precio }}</span>
                                    <span v-if="taller.precioUSD" class="text-xs text-gray-500 block">USD {{ taller.precioUSD }}</span>
                                </div>
                                <!-- Botón principal: Reservar (mobile) -->
                                <a
                                    :href="taller.botonInfo && taller.botonInfo.trim() !== '' ? `/inscripciones/${getInscripcionesId(taller.slug)}` : taller.linkTienda"
                                    :target="taller.botonInfo && taller.botonInfo.trim() !== '' ? '_self' : '_blank'"
                                    class="text-white text-[10px] font-semibold leading-[140%] text-center py-2 px-4 rounded-md uppercase transition-colors w-full block mb-2"
                                    :style="taller.colorBoton ? { backgroundColor: '#' + taller.colorBoton } : {}"
                                >
                                    {{ taller.boton }}
                                </a>
                            </div>
                         </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Talleres de Casciari 
        <div v-if="!loading && !error && casciariProducts.length > 0" class="mt-16">
            <hr class="border-t border-gray-200 mb-8">
            <h2 class="">
                <span class="text-3xl font-bold">Taller virtual con Hernán Casciari</span>
            </h2>
            <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8 mt-8">
                <div v-for="product in casciariProducts" :key="product.id" class="post-item max-w-md w-full mx-auto">
                    <div class="bg-white rounded-2xl shadow-md border border-gray-200 flex flex-col h-full">
                        <a :href="product.onlineStoreUrl" target="_blank"
                            class="block w-full aspect-square overflow-hidden rounded-t-2xl">
                            <img v-if="product.featuredImage" :src="product.featuredImage.url" :alt="product.title"
                                class="w-full h-full object-cover rounded-t-2xl" />
                        </a>
                        <h3 class="text-lg font-bold mb-2 leading-tight text-black px-4 pt-4">{{ product.title }}</h3>
                        <div class="text-gray-700 text-base mb-2 px-4 pb-4">INSCRIPCIÓN ABIERTA</div>
                        <div class="flex items-center justify-between mb-2 mt-auto px-4 pb-4">
                            <span class="text-2xl text-gray-900">{{
                                formatPrice(product.priceRange.minVariantPrice.amount) }}</span>
                            <a :href="product.onlineStoreUrl" target="_blank"
                                class="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg uppercase text-base transition-colors ml-4">
                                Reservar
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        -->
    </div>
</template>

<script>
import axios from "axios";
import { useAuthStore } from "@/stores/auth";

export default {
    name: "ProductList",
    data() {
        return {
            talleres: [],
            loading: true,
            error: null,
            filtros: {
                mes: '',
                tipo: ''
            },
            meses: [
                'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
            ],
            tiposDisponibles: [
                { value: 'masterclass', label: 'Masterclass' },
                { value: 'virtual', label: 'Virtual' }
            ],
            openSubMenu: null,
            sheetsConfig: {
                url: 'https://docs.google.com/spreadsheets/d/e/2PACX-1vTCo1hQ1saKKwgyEN_6E8Rv6WjxG3JJMdbFIx8JXBwC-ZlDJP0XYVozuxvwsMsEY_VVOylDMgnjvcqR/pub?gid=0&single=true&output=csv'
            }
        };
    },
    computed: {
        usuario() {
            return useAuthStore().usuario;
        },
        talleresFiltrados() {
            return this.talleres.filter(taller => {
                // Filtrar por mes
                if (this.filtros.mes) {
                    const mesBuscado = this.filtros.mes.toLowerCase();
                    // Mapa de meses a abreviaturas (añadir más si es necesario)
                    const abreviaturas = {
                        'enero': 'ene',
                        'febrero': 'feb',
                        'marzo': 'mar',
                        'abril': 'abr',
                        'mayo': 'may',
                        'junio': 'jun',
                        'julio': 'jul',
                        'agosto': 'ago',
                        'septiembre': 'sep',
                        'octubre': 'oct',
                        'noviembre': 'nov',
                        'diciembre': 'dic'
                    };
                    const abreviaturaBuscada = abreviaturas[mesBuscado];
                    
                    // Verificar si incluye el nombre completo o la abreviatura
                    const inicioLowerCase = taller.inicio.toLowerCase();
                    return inicioLowerCase.includes(mesBuscado) || (abreviaturaBuscada && inicioLowerCase.includes(abreviaturaBuscada));
                }
                return true;
            });
        }
    },
    methods: {
        formatPrice(price) {
            // Convertir el precio a formato de moneda pero sin símbolo
            const numPrice = parseFloat(price.replace(/[^0-9.-]/g, ''));
            return numPrice.toLocaleString('es-AR', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
            });
        },
        formatTitle(title) {
            const words = title.toUpperCase().split(' ');
            if (words.length > 3) {
                const firstHalf = words.slice(0, Math.ceil(words.length / 2)).join(' ');
                const secondHalf = words.slice(Math.ceil(words.length / 2)).join(' ');
                return `${firstHalf}\n${secondHalf}`;
            }
            return title.toUpperCase();
        },
        getInstructorName(product) {
            if (product.title.includes("con")) {
                const parts = product.title.split("con");
                if (parts.length > 1) {
                    return parts[1].trim().toUpperCase();
                }
            }
            return "DOLORES REYES";
        },
        getDateText(product) {
            return product.description.split("\n")[0];
        },
        getTimeText(product) {
            return "15 A 18 HS";
        },
        processProductEdges(edges) {
            const productos = edges.map((edge) => ({
                id: edge.node.id,
                title: edge.node.title,
                handle: edge.node.handle,
                onlineStoreUrl: edge.node.onlineStoreUrl || `https://tienda.orsai.org/products/${edge.node.handle}`,
                priceRange: edge.node.priceRange,
                featuredImage: edge.node.featuredImage || null,
                description: edge.node.description || "",
                totalInventory: edge.node.totalInventory || 0,
                tags: edge.node.tags || [],
            }));

            // Log de tags únicos
            const tagsUnicos = new Set();
            productos.forEach(producto => {
                producto.tags.forEach(tag => tagsUnicos.add(tag));
            });
            console.log('Tags únicos de Shopify:', Array.from(tagsUnicos));

            // Log de productos con sus tags
            console.log('Productos con sus tags:', productos.map(p => ({
                titulo: p.title,
                tags: p.tags
            })));

            return productos;
        },
        async fetchProducts() {
            try {
                console.log("Fetching products from Shopify...");
                const query = `
                    {
                        tallerCasciari: collection(handle: "taller-casciari") {
                            products(first: 250) {
                                edges {
                                    node {
                                        id
                                        title
                                        handle
                                        onlineStoreUrl
                                        priceRange {
                                            minVariantPrice {
                                                amount
                                                currencyCode
                                            }
                                        }
                                        featuredImage {
                                            url
                                            altText
                                        }
                                        description(truncateAt: 150)
                                        totalInventory
                                        tags
                                    }
                                }
                            }
                        }
                        escuelaOrsai: collection(handle: "escuela-prueba") {
                            products(first: 250) {
                                edges {
                                    node {
                                        id
                                        title
                                        handle
                                        onlineStoreUrl
                                        priceRange {
                                            minVariantPrice {
                                                amount
                                                currencyCode
                                            }
                                        }
                                        featuredImage {
                                            url
                                            altText
                                        }
                                        description(truncateAt: 150)
                                        totalInventory
                                        tags
                                    }
                                }
                            }
                        }
                    }
                `;

                const response = await axios.post(
                    "https://tienda.orsai.org/api/2023-10/graphql.json",
                    { query },
                    {
                        headers: {
                            "Content-Type": "application/json",
                            "X-Shopify-Storefront-Access-Token": "0601f486448cb11b7bb5991ac5b7e907",
                        },
                    }
                );

                console.log("Datos obtenidos de Shopify:", {
                    casciari: response.data.data.tallerCasciari?.products?.edges,
                    escuela: response.data.data.escuelaOrsai?.products?.edges
                });

                this.talleres = this.processProductEdges(
                    response.data.data.escuelaOrsai?.products?.edges || []
                );

            } catch (error) {
                console.error("Error fetching products:", error);
                this.error = "Error al cargar los productos: " + error.message;
            } finally {
                this.loading = false;
            }
        },
        toggleSubMenu(tipo) {
            if (this.openSubMenu === tipo) {
                this.openSubMenu = null;
            } else {
                this.openSubMenu = tipo;
            }
        },
        seleccionarMes(mes) {
            this.filtros.mes = mes;
            this.openSubMenu = null;
        },
        seleccionarTipo(tipo) {
            this.filtros.tipo = tipo;
            this.openSubMenu = null;
        },
        closeAllMenus() {
            this.openSubMenu = null;
        },
        limpiarFiltros() {
            this.filtros.mes = '';
            this.filtros.tipo = '';
            this.openSubMenu = null;
        },
        // Función para mapear slug a ID de inscripciones
        getInscripcionesId(slug) {
            // Mapeo de slugs a IDs numéricos para inscripciones
            const slugToIdMapping = {
                'taller-1': 1,
                'taller-2': 2,
                'taller-3': 3,
                'taller-4': 4,
                'taller-18': 18,
                // Agregar más mapeos según sea necesario
            };
            
            // Si el slug es un número, usarlo directamente
            if (!isNaN(slug) && slug !== '') {
                return slug;
            }
            
            // Si no es un número, buscar en el mapeo
            return slugToIdMapping[slug] || slug;
        },
        async fetchDataFromSheets() {
            try {
                const response = await axios.get(this.sheetsConfig.url);
                // Usar papaparse para parsear el CSV correctamente
                const Papa = (await import('papaparse')).default;
                const parsed = Papa.parse(response.data, { header: true, skipEmptyLines: true });
                this.talleres = parsed.data
                    .map((row, idx) => {
                        // Aseguramos que el ID sea un número válido (mantener para compatibilidad)
                        const idNum = Number((row.ID || '').toString().trim());
                        // Usar la nueva columna SLUG para el identificador
                        const slug = (row.SLUG || '').toString().trim();
                        let urlAfiche = (row.URL_AFICHE || '').trim().replace(/^"(.*)"$/, '$1');
                        try {
                            if (urlAfiche) new URL(urlAfiche);
                        } catch {
                            urlAfiche = '';
                        }
                        return {
                            id: idNum, // Mantener ID para compatibilidad
                            slug: slug, // Nueva propiedad slug
                            docente: row.DOCENTE || '',
                            lema: row.LEMA || '',
                            inicio: row.INICIO || '',
                            precio: this.formatPrice(row.ARS || '0'),
                            precioUSD: row.USD || '',
                            linkTienda: row.LINK_TIENDA || '',
                            urlAfiche,
                            boton: row.BOTON || 'Reservar',
                            botonInfo: row.BOTON_INFO,
                            colorBoton: row.COLOR_BOTON
                        };
                    })
                    .filter(taller => !isNaN(taller.id) && taller.slug) // Filtrar por ID válido y slug presente
                    .sort((a, b) => a.id - b.id);
                console.log('Talleres cargados desde Google Sheets:', this.talleres);
                // Log adicional para verificar la estructura de los datos
                console.log('Estructura de talleres con slug:', this.talleres.map(t => ({
                    id: t.id,
                    slug: t.slug,
                    docente: t.docente,
                    lema: t.lema,
                    botonInfo: t.botonInfo
                })));
            } catch (error) {
                console.error("Error fetching data from Google Sheets:", error);
                this.error = "Error al cargar los talleres: " + error.message;
            } finally {
                this.loading = false;
            }
        },
        handleImageError(e) {
            if (!e.target) return;
            
            console.warn('Error cargando imagen:', e.target.src);
            
            // Crear el elemento de fallback
            const fallback = document.createElement('div');
            fallback.className = 'w-full aspect-square bg-gray-100 flex items-center justify-center rounded-t-2xl';
            fallback.innerHTML = '<span class="text-gray-400">Imagen no disponible</span>';
            
            // Reemplazar la imagen con el fallback
            if (e.target.parentNode) {
                e.target.parentNode.replaceChild(fallback, e.target);
            }
        }
    },
    async mounted() {
        await this.fetchDataFromSheets();
    },
};
</script>

<style scoped>
.product-title {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>

<script setup>
// Helper para cupo máximo
const getCupoMaximo = (product) => product.cupoMaximo || 100;
</script>